import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';

import { MembershipV2Service } from 'src/app/services/membership-v2.service';
import { AuthService } from 'src/app/services/auth.service';
import { KundeKortService } from 'src/app/services/kunde-kort.service';
import { CompanysitesService } from 'src/app/admin-dashboard/company-sites/company-sites.service';

import { EmployeeDialogComponent } from './dialogs/employee-dialog.component';
import { GroupDialogComponent } from './dialogs/group-dialog.component';
import { LedgerDialogComponent } from './dialogs/ledger-dialog.component';
import { CreditDialogComponent } from './dialogs/credit-dialog.component';
import { ImportEmployeesDialogComponent } from './dialogs/import-employees-dialog.component';

// Production v2 (root-collection) Membership admin: employees + groups for the
// isolated MembershipV2Service. Nothing here touches the legacy Users / User
// Group pages. Billing and customer↔group mapping now live as their own
// top-level pages (v2-billing / customer-mapping). Material UI (tabs +
// sortable/paginated tables + dialogs) matching the rest of the admin.
@Component({
  selector: 'membership-v2',
  templateUrl: './membership-v2.component.html',
  styleUrls: ['./membership-v2.component.css']
})
export class MembershipV2Component implements OnInit, OnDestroy {

  siteId: string;
  adminEmail = '';

  employees = new MatTableDataSource<any>([]);
  groups = new MatTableDataSource<any>([]);
  groupArray: any[] = [];          // plain list for group selects + name lookups
  customers: any[] = [];
  systems: any[] = [];

  empColumns = ['name', 'email', 'employeeCode', 'group', 'credit', 'status', 'actions'];
  groupColumns = ['name', 'groupId', 'type', 'amount', 'customer', 'systems', 'accumulate', 'actions'];

  // Tables live inside mat-tabs, so their sort/paginator only exist once the tab
  // renders — assign via setters when the view child appears.
  // Kept so a programmatic filter (duplicate add) also shows in the search box —
  // otherwise the table silently narrows with an empty-looking search field.
  @ViewChild('empSearchInput') empSearchInput: ElementRef<HTMLInputElement>;

  @ViewChild('empSort') set empSort(s: MatSort) { if (s) { this.employees.sort = s; } }
  @ViewChild('empPaginator') set empPaginator(p: MatPaginator) { if (p) { this.employees.paginator = p; } }
  @ViewChild('groupSort') set groupSort(s: MatSort) { if (s) { this.groups.sort = s; } }
  @ViewChild('groupPaginator') set groupPaginator(p: MatPaginator) { if (p) { this.groups.paginator = p; } }

  private subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private svc: MembershipV2Service,
    private auth: AuthService,
    private kundeKort: KundeKortService,
    private companySites: CompanysitesService,
    private dialog: MatDialog
  ) { }

  ngOnInit() {
    this.employees.filterPredicate = (m: any, filter: string) =>
      `${m.name || ''} ${m.email || ''} ${m.employeeCode || ''}`.toLowerCase().includes(filter);

    this.subs.push(this.auth.user$.subscribe(u => this.adminEmail = u ? u.email : ''));
    this.subs.push(this.kundeKort.getCustomers().subscribe((c: any[]) => this.customers = c));

    const source = this.route.parent ? this.route.parent.params : this.route.params;
    this.subs.push(source.subscribe((p: Params) => {
      this.siteId = p['siteId'];
      if (!this.siteId) { return; }
      this.subs.push(this.svc.getMemberships$(this.siteId).subscribe((rows: any[]) => this.employees.data = rows));
      this.subs.push(this.svc.getGroups$(this.siteId).subscribe((rows: any[]) => {
        this.groupArray = rows;
        this.groups.data = rows;
      }));
      this.subs.push(this.companySites.getSiteSystemsByIdObs(this.siteId).subscribe((s: any[]) => this.systems = s));
    }));
  }

  ngOnDestroy() { this.subs.forEach(s => s.unsubscribe()); }

  // ---- lookups / labels --------------------------------------------------
  applyEmpFilter(event: Event) {
    this.employees.filter = (event.target as HTMLInputElement).value.trim().toLowerCase();
    if (this.employees.paginator) { this.employees.paginator.firstPage(); }
  }
  groupName(groupId: string) {
    const g = this.groupArray.find(x => x.groupId === groupId);
    return g ? g.name : (groupId || '—');
  }
  customerName(id: string) {
    const c = this.customers.find(x => x.kundekortId === id);
    return c ? c.name : (id ? '(unknown)' : '—');
  }
  systemName(systemId: string) {
    const s = this.systems.find(x => x.systemId === systemId);
    return s ? s.name : systemId;
  }
  systemsLabel(g: any) {
    const ids = g.systemIds || [];
    if (!ids.length) { return '—'; }
    return ids.map((id: string) => this.systemName(id)).join(', ');
  }
  groupAmount(g: any) {
    return g.worker === 'subsidyGroup' ? `${Number(g.subsidy) || 0}%` : (Number(g.credit) || 0);
  }
  groupType(g: any) {
    return g.worker === 'subsidyGroup' ? 'Subsidy' : 'Credit Renew';
  }

  // ---- employees ---------------------------------------------------------
  addEmployee() {
    const ref = this.dialog.open(EmployeeDialogComponent, {
      width: '460px',
      data: { mode: 'add', groups: this.groupArray }
    });
    ref.afterClosed().subscribe(res => {
      if (res && res.action === 'add') {
        this.svc.addEmployee(this.siteId, res.payload, res.isTest).then(result => {
          // Rejected as a duplicate: point the table at the row that blocked it
          // so the admin can see and edit it instead of guessing.
          if (result && result.existing) {
            const term = (result.existing.email || result.existing.name || '').toLowerCase();
            if (term) {
              this.employees.filter = term;
              if (this.empSearchInput) { this.empSearchInput.nativeElement.value = term; }
              if (this.employees.paginator) { this.employees.paginator.firstPage(); }
            }
          }
        });
      }
    });
  }
  importEmployees() {
    this.dialog.open(ImportEmployeesDialogComponent, {
      width: '640px',
      disableClose: true,
      data: { siteId: this.siteId, groups: this.groupArray, existingEmails: this.employees.data.map(m => m.email).filter(e => !!e) }
    });
  }
  editEmployee(m: any) {
    const ref = this.dialog.open(EmployeeDialogComponent, {
      width: '460px',
      data: { mode: 'edit', employee: m, groups: this.groupArray }
    });
    ref.afterClosed().subscribe(res => {
      if (res && res.action === 'edit') {
        this.svc.updateMembership(m.dbid, res.patch);
      }
    });
  }
  openCredit(m: any) {
    const ref = this.dialog.open(CreditDialogComponent, {
      width: '360px',
      data: { name: m.name || m.email, credit: m.credit }
    });
    ref.afterClosed().subscribe(res => {
      if (res && res.amount > 0) {
        this.svc.adjustCredit(m.dbid, m.credit, res.type, res.amount, this.adminEmail).catch(() => { });
      }
    });
  }
  openLedger(m: any) {
    this.dialog.open(LedgerDialogComponent, {
      width: '640px',
      data: { name: m.name || m.email, credit: m.credit, transactions$: this.svc.getMembershipTransactions$(m.dbid) }
    });
  }
  toggleActive(m: any) { this.svc.updateMembership(m.dbid, { status: !m.status }); }
  deleteEmployee(m: any) {
    if (confirm(`Remove membership for "${m.name || m.email}" from this site? The record is kept (soft delete), not erased.`)) {
      this.svc.softDeleteMembership(m.dbid);
    }
  }

  // ---- groups ------------------------------------------------------------
  addGroup() {
    const ref = this.dialog.open(GroupDialogComponent, {
      width: '520px',
      data: { mode: 'add', customers: this.customers, systems: this.systems }
    });
    ref.afterClosed().subscribe(res => {
      if (res && res.action === 'add') {
        this.svc.addGroup(this.siteId, res.payload, res.isTest);
      }
    });
  }
  editGroup(g: any) {
    const ref = this.dialog.open(GroupDialogComponent, {
      width: '520px',
      data: { mode: 'edit', group: g, customers: this.customers, systems: this.systems }
    });
    ref.afterClosed().subscribe(res => {
      if (res && res.action === 'edit') {
        // update the group doc, then reconcile its renewal task (create/update
        // for Credit-Renew, delete if switched to Subsidy). Merge g + patch so
        // the sync has siteId + the new worker/schedule.
        this.svc.updateGroup(g.dbid, res.patch)
          .then(() => this.svc.syncRenewalTask(g.dbid, { ...g, ...res.patch }, true))
          .catch(() => { });
      }
    });
  }
  deleteGroup(g: any) {
    if (confirm(`Remove group "${g.name}"? The record is kept (soft delete), not erased.`)) {
      this.svc.softDeleteGroup(g.dbid);
    }
  }
}
